import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Badge } from './ui/badge';

interface AboutProps {
  title: string;
  content: string;
  mission: string;
  vision: string;
  experience: string;
  image: string;
  stats?: {
    projectsCompleted: string;
    clientSatisfaction: string;
    yearsExperience: string;
  };
}

const About: React.FC<AboutProps> = ({
  title,
  content,
  mission,
  vision,
  experience,
  image,
  stats: statsProp
}) => {
  const stats = useMemo(() => [
    { raw: statsProp?.projectsCompleted || '0', label: 'Projects Completed' },
    { raw: statsProp?.clientSatisfaction || '0', label: 'Client Satisfaction' },
    { raw: statsProp?.yearsExperience || '0', label: 'Years Experience' }
  ], [statsProp?.projectsCompleted, statsProp?.clientSatisfaction, statsProp?.yearsExperience]);

  const [visible, setVisible] = useState(false);
  const [counters, setCounters] = useState([0, 0, 0]);
  const [done, setDone] = useState([false, false, false]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Reset counters and done when stats change or when section becomes visible
    setCounters([0, 0, 0]);
    setDone([false, false, false]);
    if (!visible) return;
    const intervals: NodeJS.Timeout[] = [];
    stats.forEach((stat, i) => {
      const match = stat.raw.match(/(\d+)/);
      const value = match ? parseInt(match[1]) : 0;
      let current = 0;
      const increment = Math.ceil(value / 40);
      intervals[i] = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCounters(counters => {
            const newCounters = [...counters];
            newCounters[i] = value;
            return newCounters;
          });
          setDone(done => {
            const newDone = [...done];
            newDone[i] = true;
            return newDone;
          });
          clearInterval(intervals[i]);
        } else {
          setCounters(counters => {
            const newCounters = [...counters];
            newCounters[i] = current;
            return newCounters;
          });
        }
      }, 20);
    });
    return () => intervals.forEach(clearInterval);
  }, [visible, stats[0].raw, stats[1].raw, stats[2].raw]);

  return (
    <section
      id="about"
      ref={sectionRef}
      className={`py-20 lg:py-40 bg-gray-100 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-black/10 via-black/5 to-transparent z-10 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300" />
            <img
              src={image}
              alt="Construction site"
              className="rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 w-full h-[500px] object-cover"
            />
          </div>

          <div className="flex flex-col gap-8">
            <div className="text-center">
              <h2 className="text-4xl font-bold mb-6">{title}</h2>
              <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {content}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-4 bg-gray-50 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300">
                  <div className="text-3xl font-bold text-primary-500 mb-2">
                    {done[index] ? stat.raw : `${counters[index]}${stat.raw.replace(/\d+/g, '')}`}
                  </div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300">
                <h3 className="text-xl font-semibold mb-4">Our Mission</h3>
                <p className="text-gray-600">{mission}</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300">
                <h3 className="text-xl font-semibold mb-4">Our Expertise</h3>
                <p className="text-gray-600">{vision}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About; 